import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel,
  Divider,
  Stack,
  TableContainer,
  Paper,
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  Payment as PaymentIcon,
  CheckCircle as CheckCircleIcon,
  Email as EmailIcon,
  Event as EventIcon,
  PlayCircle as PlayCircleIcon,
  Sync as SyncIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { invoiceService } from '../services';

// Utilitários para formatação
function formatDate(date: string | Date | undefined): string {
  if (!date) return 'N/A';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return 'N/A';
  return d.toLocaleDateString('pt-BR');
}

function formatCurrency(value: number | undefined): string {
  if (!value) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

function formatCpfCnpj(value: string | null | undefined): string {
  if (!value) return 'N/A';
  const clean = value.replace(/\D/g, '');
  if (clean.length === 11) {
    return clean.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }
  if (clean.length === 14) {
    return clean.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }
  return value;
}

interface InvoiceNfse {
  uuid: string | null;
  status: string | null;
  chaveAcesso: string | null;
  errorMessage: string | null;
  valor: number | null;
}

interface InvoiceEvent {
  eventId: string;
  eventTitle: string;
  eventSlug: string;
  startDate?: Date;
  endDate: Date;
  venue?: {
    startDate?: Date;
    endDate?: Date;
    address: string | null;
    location: string | null;
    city: string | null;
    state: string | null;
    zipCode: string | null;
  };
  organizer: {
    name: string;
    cpfCnpj: string | null;
    personType: 'fisica' | 'juridica' | null;
    email: string | null;
    phone: string | null;
    address: string | null;
    city: string | null;
    state: string | null;
    zipCode: string | null;
    street?: string | null;
    streetNumber?: string | null;
    neighborhood?: string | null;
    ibgeMunicipioCode?: string | null;
  };
  totalAmountPaid: number;
  totalRegistrations: number;
  feePercentage: number;
  parishFeeAmount: number;
  platformFeeAmount?: number;
  pagamentos?: {
    pix: { count: number; totalAmount: number };
    cartao: { count: number; totalAmount: number };
    outros?: { count: number; totalAmount: number };
  };
  invoiceIssued: boolean;
  invoicePaid: boolean;
  invoiceNumber: string | null;
  invoiceDate: Date | null;
  paymentDate: Date | null;
  paymentMethod: string | null;
  nfse?: InvoiceNfse | null;
}

function ymdSaoPaulo(date: string | Date | undefined): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(d);
}

function nfseBusy(status?: string | null): boolean {
  return status === 'pendente' || status === 'processando';
}

type EmitForm = {
  documento: string;
  nome: string;
  email: string;
  logradouro: string;
  numero: string;
  bairro: string;
  uf: string;
  cep: string;
  codigoMunicipio: string;
  eventoNome: string;
  dataInicio: string;
  dataFim: string;
  eventoCep: string;
  eventoLogradouro: string;
  eventoNumero: string;
  eventoComplemento: string;
  eventoBairro: string;
};

function suggestStreetParts(address?: string | null): { logradouro: string; numero: string } {
  const raw = String(address || '').trim();
  if (!raw) return { logradouro: '', numero: '' };
  const match = raw.match(/^(.*?)[,\s]+(?:n[ºo°.]?\s*)?(\d+[A-Za-z]?|s\/n)\b/i);
  if (!match) return { logradouro: raw, numero: '' };
  return {
    logradouro: match[1].replace(/[,\s]+$/, '').trim(),
    numero: match[2],
  };
}

function formFromEvent(event: InvoiceEvent): EmitForm {
  const venueParts = suggestStreetParts(event.venue?.address);
  return {
    documento: (event.organizer.cpfCnpj || '').replace(/\D/g, ''),
    nome: event.organizer.name || '',
    email: event.organizer.email || '',
    logradouro: event.organizer.street || event.organizer.address || '',
    numero: event.organizer.streetNumber || '',
    bairro: event.organizer.neighborhood || '',
    uf: (event.organizer.state || '').slice(0, 2).toUpperCase(),
    cep: (event.organizer.zipCode || '').replace(/\D/g, ''),
    codigoMunicipio: (event.organizer.ibgeMunicipioCode || '').replace(/\D/g, ''),
    eventoNome: event.eventTitle,
    dataInicio: ymdSaoPaulo(event.venue?.startDate || event.startDate),
    dataFim: ymdSaoPaulo(event.venue?.endDate || event.endDate),
    eventoCep: (event.venue?.zipCode || '').replace(/\D/g, ''),
    eventoLogradouro: venueParts.logradouro || event.venue?.address || '',
    eventoNumero: venueParts.numero,
    eventoComplemento: event.venue?.location || '',
    eventoBairro: '',
  };
}

/** Chave YYYY-MM a partir da data de término do evento. */
function endMonthKey(endDate: string | Date | undefined): string {
  if (!endDate) return 'sem-data';
  const d = typeof endDate === 'string' ? new Date(endDate) : endDate;
  if (isNaN(d.getTime())) return 'sem-data';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function endMonthLabel(key: string): string {
  if (key === 'sem-data') return 'Sem data de término';
  const [year, month] = key.split('-').map(Number);
  const label = new Date(year, month - 1, 1).toLocaleDateString('pt-BR', {
    month: 'long',
    year: 'numeric',
  });
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function groupEventsByEndMonth(
  events: InvoiceEvent[],
  ascending: boolean
): Array<{ monthKey: string; label: string; events: InvoiceEvent[] }> {
  const map = new Map<string, InvoiceEvent[]>();
  for (const event of events) {
    const key = endMonthKey(event.endDate);
    const list = map.get(key) || [];
    list.push(event);
    map.set(key, list);
  }

  const keys = Array.from(map.keys()).sort((a, b) => {
    if (a === 'sem-data') return 1;
    if (b === 'sem-data') return -1;
    return ascending ? a.localeCompare(b) : b.localeCompare(a);
  });

  return keys.map((monthKey) => ({
    monthKey,
    label: endMonthLabel(monthKey),
    events: (map.get(monthKey) || []).sort((a, b) => {
      const da = new Date(a.endDate).getTime();
      const db = new Date(b.endDate).getTime();
      return ascending ? da - db : db - da;
    }),
  }));
}

const InvoiceIssuance: React.FC = () => {
  const queryClient = useQueryClient();
  // Obter mês atual no formato YYYY-MM
  const [activeTab, setActiveTab] = useState<0 | 1>(0); // 0 = finalizados, 1 = em curso
  const [filters, setFilters] = useState({
    page: 1,
    limit: 200,
    eventId: '',
    parishId: '',
    month: '',
    status: 'finalizados' as 'finalizados' | 'em_curso',
  });

  const handleTabChange = (_: React.SyntheticEvent, newValue: 0 | 1) => {
    setActiveTab(newValue);
    const status = newValue === 0 ? 'finalizados' : 'em_curso';
    setFilters((prev) => ({ ...prev, status }));
  };
  const [selectedEvent, setSelectedEvent] = useState<InvoiceEvent | null>(null);
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [markPaidDialog, setMarkPaidDialog] = useState(false);
  const [registerInvoiceDialog, setRegisterInvoiceDialog] = useState(false);
  const [emitDialog, setEmitDialog] = useState(false);
  const [emitForm, setEmitForm] = useState<EmitForm | null>(null);
  const [markPaidData, setMarkPaidData] = useState({
    paymentDate: new Date().toISOString().split('T')[0],
    paymentMethod: '',
    notes: '',
  });
  const DEFAULT_EMAILS = [
    'roubertpi@gmail.com',
    'meusacramento@gmail.com',
    'eduardo@bastoscontabil.com.br',
  ];
  const [registerInvoiceData, setRegisterInvoiceData] = useState({
    invoiceNumber: '',
    invoiceDate: new Date().toISOString().split('T')[0],
    notes: '',
  });
  const [requestDataDialog, setRequestDataDialog] = useState<{
    open: boolean;
    event: InvoiceEvent | null;
    mode: 'manual' | 'email';
    cpfCnpj: string;
  }>({ open: false, event: null, mode: 'manual', cpfCnpj: '' });
  const [emailRecipients, setEmailRecipients] = useState<Record<string, boolean>>(() =>
    DEFAULT_EMAILS.reduce((acc, e) => ({ ...acc, [e]: true }), {})
  );
  const [extraEmails, setExtraEmails] = useState<string[]>([]);
  const [newEmailInput, setNewEmailInput] = useState('');
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Fetch events for invoice
  const { data: invoiceData, isLoading, error } = useQuery({
    queryKey: ['admin-invoices', filters],
    queryFn: () => invoiceService.getEventsForInvoice(filters),
  });

  // Mark as paid mutation
  const markAsPaidMutation = useMutation({
    mutationFn: ({ eventId, data }: { eventId: string; data: any }) =>
      invoiceService.markInvoiceAsPaid(eventId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-invoices'] });
      setSnackbar({ open: true, message: 'Nota marcada como paga com sucesso.', severity: 'success' });
      setMarkPaidDialog(false);
      setSelectedEvent(null);
      setMarkPaidData({
        paymentDate: new Date().toISOString().split('T')[0],
        paymentMethod: '',
        notes: '',
      });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Erro ao marcar nota como paga.',
        severity: 'error',
      });
    },
  });

  // Register invoice mutation
  const registerInvoiceMutation = useMutation({
    mutationFn: ({ eventId, data }: { eventId: string; data: any }) =>
      invoiceService.registerInvoiceIssuance(eventId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-invoices'] });
      setSnackbar({ open: true, message: 'Emissão de nota registrada com sucesso.', severity: 'success' });
      setRegisterInvoiceDialog(false);
      setSelectedEvent(null);
      setRegisterInvoiceData({
        invoiceNumber: '',
        invoiceDate: new Date().toISOString().split('T')[0],
        notes: '',
      });
      setExtraEmails([]);
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Erro ao registrar emissão de nota.',
        severity: 'error',
      });
    },
  });

  const emitNfseMutation = useMutation({
    mutationFn: ({ eventId, data }: { eventId: string; data: Record<string, unknown> }) =>
      invoiceService.emitPlatformFeeNfse(eventId, data),
    onSuccess: (result: any) => {
      queryClient.invalidateQueries({ queryKey: ['admin-invoices'] });
      setEmitDialog(false);
      setSnackbar({ open: true, message: result?.message || 'Emissão solicitada.', severity: 'success' });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Erro ao emitir a nota.',
        severity: 'error',
      });
    },
  });

  const refreshNfseMutation = useMutation({
    mutationFn: (eventId: string) => invoiceService.refreshPlatformFeeNfse(eventId),
    onSuccess: (result: any) => {
      queryClient.invalidateQueries({ queryKey: ['admin-invoices'] });
      const failed = result?.data?.status === 'rejeitada' || result?.data?.status === 'erro';
      setSnackbar({
        open: true,
        message: result?.message || 'Status atualizado.',
        severity: failed ? 'error' : 'success',
      });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Erro ao consultar a nota.',
        severity: 'error',
      });
    },
  });

  const handleMarkAsPaid = (event: InvoiceEvent) => {
    setSelectedEvent(event);
    setMarkPaidDialog(true);
  };

  const handleOpenDetails = (event: InvoiceEvent) => {
    setSelectedEvent(event);
    setDetailsDialog(true);
  };

  const handleEmitInvoice = (event: InvoiceEvent) => {
    setSelectedEvent(event);
    setEmitForm(formFromEvent(event));
    setEmitDialog(true);
  };

  const handleSubmitEmit = () => {
    if (!selectedEvent || !emitForm) return;
    emitNfseMutation.mutate({
      eventId: selectedEvent.eventId,
      data: {
        tomador: {
          documento: emitForm.documento,
          nome: emitForm.nome,
          email: emitForm.email,
          logradouro: emitForm.logradouro,
          numero: emitForm.numero,
          bairro: emitForm.bairro,
          uf: emitForm.uf,
          cep: emitForm.cep,
          codigoMunicipio: emitForm.codigoMunicipio,
        },
        atividadeEvento: {
          nome: emitForm.eventoNome,
          dataInicio: emitForm.dataInicio,
          dataFim: emitForm.dataFim,
          endereco: {
            cep: emitForm.eventoCep,
            logradouro: emitForm.eventoLogradouro,
            numero: emitForm.eventoNumero,
            complemento: emitForm.eventoComplemento,
            bairro: emitForm.eventoBairro,
          },
        },
      },
    });
  };

  const handleDownloadDanfe = async (eventId: string) => {
    try {
      const blob = await invoiceService.downloadPlatformFeeDanfe(eventId);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `nfse-${eventId}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      setSnackbar({ open: true, message: 'Não foi possível baixar o DANFE.', severity: 'error' });
    }
  };

  const handleRegisterInvoice = (event: InvoiceEvent) => {
    setSelectedEvent(event);
    setEmailRecipients(DEFAULT_EMAILS.reduce((acc, e) => ({ ...acc, [e]: true }), {}));
    setExtraEmails([]);
    setNewEmailInput('');
    setRegisterInvoiceDialog(true);
  };

  const toggleEmailRecipient = (email: string, checked: boolean) => {
    setEmailRecipients((prev) => ({ ...prev, [email]: checked }));
  };

  const addExtraEmail = () => {
    const email = newEmailInput.trim().toLowerCase();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
    if (DEFAULT_EMAILS.includes(email) || extraEmails.includes(email)) return;
    setExtraEmails((prev) => [...prev, email]);
    setEmailRecipients((prev) => ({ ...prev, [email]: true }));
    setNewEmailInput('');
  };

  const removeExtraEmail = (email: string) => {
    setExtraEmails((prev) => prev.filter((e) => e !== email));
    setEmailRecipients((prev) => {
      const next = { ...prev };
      delete next[email];
      return next;
    });
  };

  const handleSubmitMarkAsPaid = () => {
    if (!selectedEvent) return;
    markAsPaidMutation.mutate({
      eventId: selectedEvent.eventId,
      data: markPaidData,
    });
  };

  const handleSubmitRegisterInvoice = () => {
    if (!selectedEvent) return;
    const recipientEmails = [
      ...DEFAULT_EMAILS.filter((e) => emailRecipients[e]),
      ...extraEmails.filter((e) => emailRecipients[e]),
    ];
    registerInvoiceMutation.mutate({
      eventId: selectedEvent.eventId,
      data: { ...registerInvoiceData, recipientEmails },
    });
  };

  // Request invoice data mutation
  const requestInvoiceDataMutation = useMutation({
    mutationFn: (eventId: string) => invoiceService.requestInvoiceData(eventId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-invoices'] });
      setSnackbar({ open: true, message: 'Solicitação enviada com sucesso para a paróquia.', severity: 'success' });
      setRequestDataDialog({ open: false, event: null, mode: 'manual', cpfCnpj: '' });
    },
    onError: (error: any) => {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Erro ao solicitar dados fiscais.', severity: 'error' });
    },
  });

  const fillInvoiceDataManualMutation = useMutation({
    mutationFn: ({ eventId, cpfCnpj }: { eventId: string; cpfCnpj: string }) =>
      invoiceService.fillInvoiceDataManual(eventId, cpfCnpj),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-invoices'] });
      setSnackbar({ open: true, message: 'Dados preenchidos e cadastro atualizado com sucesso.', severity: 'success' });
      setRequestDataDialog({ open: false, event: null, mode: 'manual', cpfCnpj: '' });
    },
    onError: (error: any) => {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Erro ao preencher dados manualmente.', severity: 'error' });
    },
  });

  const handleRequestInvoiceData = (event: InvoiceEvent) => {
    setRequestDataDialog({ open: true, event, mode: 'manual', cpfCnpj: '' });
  };

  const submitRequestDataByEmail = () => {
    if (!requestDataDialog.event) return;
    requestInvoiceDataMutation.mutate(requestDataDialog.event.eventId);
  };

  const submitRequestDataManual = () => {
    if (!requestDataDialog.event) return;
    if (!requestDataDialog.cpfCnpj.trim()) {
      setSnackbar({ open: true, message: 'Informe CPF/CNPJ para continuar.', severity: 'error' });
      return;
    }

    fillInvoiceDataManualMutation.mutate({
      eventId: requestDataDialog.event.eventId,
      cpfCnpj: requestDataDialog.cpfCnpj.trim(),
    });
  };

  const [backfillingSlug, setBackfillingSlug] = useState<string | null>(null);
  const backfillMutation = useMutation({
    mutationFn: (slug: string) => {
      setBackfillingSlug(slug);
      return invoiceService.backfillPaymentMethod(slug);
    },
    onSuccess: (data) => {
      setBackfillingSlug(null);
      queryClient.invalidateQueries({ queryKey: ['admin-invoices'] });
      const d = data?.data;
      setSnackbar({
        open: true,
        message: d
          ? `Atualizados ${d.updated} de ${d.total} inscrições. ${d.failed} falharam.`
          : 'Formas de pagamento atualizadas com sucesso.',
        severity: 'success',
      });
    },
    onError: (error: any) => {
      setBackfillingSlug(null);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Erro ao buscar formas de pagamento.',
        severity: 'error',
      });
    },
  });

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">Erro ao carregar dados de emissão de notas.</Alert>
      </Box>
    );
  }

  const events = invoiceData?.data?.events || [];
  const summary = invoiceData?.data?.summary as
    | { valorTotal: number; valorPlataforma: number; valorParoquia: number; totalPix: number; totalCartao: number; totalOutros?: number }
    | undefined;
  const eventsByMonth = groupEventsByEndMonth(events, filters.status === 'em_curso');

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Emissão de Notas
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Organizado pelo mês em que o evento terminou. Emissão sobre a taxa de 6,9% do valor arrecadado.
      </Typography>

      {/* Abas */}
      <Tabs value={activeTab} onChange={handleTabChange} sx={{ mt: 2, mb: 1 }}>
        <Tab
          icon={<EventIcon />}
          iconPosition="start"
          label="Eventos finalizados"
          id="tab-finalizados"
          aria-controls="panel-finalizados"
        />
        <Tab
          icon={<PlayCircleIcon />}
          iconPosition="start"
          label="Em curso"
          id="tab-em-curso"
          aria-controls="panel-em-curso"
        />
      </Tabs>

      {/* Relatório resumido */}
      {summary && events.length > 0 && (
        <Card sx={{ mt: 3, mb: 3, bgcolor: 'grey.50' }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Resumo do período
            </Typography>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={6} sm={4} md={2}>
                <Typography variant="caption" color="text.secondary">Valor total</Typography>
                <Typography variant="h6" color="primary">{formatCurrency(summary.valorTotal)}</Typography>
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <Typography variant="caption" color="text.secondary">Plataforma (fee do evento)</Typography>
                <Typography variant="h6">{formatCurrency(summary.valorPlataforma)}</Typography>
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <Typography variant="caption" color="text.secondary">Paróquia (valor restante)</Typography>
                <Typography variant="h6" color="success.main">{formatCurrency(summary.valorParoquia)}</Typography>
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <Typography variant="caption" color="text.secondary">PIX</Typography>
                <Typography variant="h6">{formatCurrency(summary.totalPix)}</Typography>
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <Typography variant="caption" color="text.secondary">Cartão</Typography>
                <Typography variant="h6">{formatCurrency(summary.totalCartao)}</Typography>
              </Grid>
              {(summary?.totalOutros ?? 0) > 0 && (
                <Grid item xs={6} sm={4} md={2}>
                  <Typography variant="caption" color="text.secondary">Outros / Não informado</Typography>
                  <Typography variant="h6">{formatCurrency(summary?.totalOutros ?? 0)}</Typography>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Filtros */}
      <Card sx={{ mt: 3, mb: 3 }}>
        <CardContent>
          <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
            <TextField
              label="ID do Evento"
              value={filters.eventId}
              onChange={(e) => setFilters({ ...filters, eventId: e.target.value })}
              size="small"
            />
            <TextField
              label="ID da Paróquia"
              value={filters.parishId}
              onChange={(e) => setFilters({ ...filters, parishId: e.target.value })}
              size="small"
            />
            <TextField
              label="Mês que o evento terminou"
              type="month"
              value={filters.month}
              onChange={(e) => setFilters({ ...filters, month: e.target.value, page: 1 })}
              size="small"
              InputLabelProps={{ shrink: true }}
              helperText={
                !filters.month
                  ? 'Todos os meses (agrupados pela data de término)'
                  : filters.status === 'em_curso'
                    ? 'Filtra pelo mês em que o evento termina'
                    : 'Filtra pelo mês em que o evento terminou'
              }
              sx={{ minWidth: 220 }}
            />
            <Button variant="contained" onClick={() => queryClient.invalidateQueries({ queryKey: ['admin-invoices'] })}>
              Buscar
            </Button>
            {filters.month && (
              <Button 
                variant="outlined" 
                onClick={() => setFilters({ ...filters, month: '' })}
              >
                Limpar Filtro de Mês
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Lista de Eventos */}
      {events.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="body1" color="text.secondary" align="center">
              {filters.status === 'em_curso'
                ? 'Nenhum evento em curso no momento.'
                : 'Nenhum evento encontrado para emissão de notas.'}
            </Typography>
            {filters.month && (
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                Use &quot;Limpar Filtro de Mês&quot; para ver todos.
              </Typography>
            )}
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={2}>
          {eventsByMonth.map((group) => {
            const monthTotal = group.events.reduce((s, e) => s + (e.totalAmountPaid || 0), 0);
            const monthPlatform = group.events.reduce((s, e) => s + (e.platformFeeAmount || 0), 0);
            return (
              <Card key={group.monthKey} sx={{ borderRadius: 3 }}>
                <Box
                  sx={{
                    px: 2.5,
                    py: 1.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'grey.50',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 1,
                  }}
                >
                  <Typography variant="subtitle1" fontWeight={700}>
                    {group.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {group.events.length} evento(s) · Total {formatCurrency(monthTotal)} · Plataforma{' '}
                    {formatCurrency(monthPlatform)}
                  </Typography>
                </Box>
                <TableContainer component={Paper} elevation={0}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ minWidth: 360 }}>Evento e Paróquia</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell sx={{ minWidth: 220 }}>Meios de pagamento</TableCell>
                        <TableCell sx={{ minWidth: 220 }}>Receitas</TableCell>
                        <TableCell sx={{ minWidth: 190 }}>Ações</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {group.events.map((event: InvoiceEvent) => (
                  <TableRow
                    key={event.eventId}
                    hover
                    onClick={() => handleOpenDetails(event)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell sx={{ verticalAlign: 'top' }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: 2,
                            bgcolor: '#F3EEFF',
                            color: '#4b1b7a',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mt: 0.25,
                            flexShrink: 0,
                          }}
                        >
                          <EventIcon fontSize="small" />
                        </Box>
                        <Box>
                          <Typography variant="body2" fontWeight={700}>
                            {event.eventTitle}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {event.organizer.name} · {event.totalRegistrations} inscrição(ões)
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {filters.status === 'em_curso' ? `Termina em ${formatDate(event.endDate)}` : `Finalizado em ${formatDate(event.endDate)}`}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {formatCpfCnpj(event.organizer.cpfCnpj)}
                          </Typography>
                          <Typography variant="caption" color="primary.main" display="block" sx={{ mt: 0.5 }}>
                            Clique para ver detalhes
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top' }}>
                      {filters.status === 'em_curso' ? (
                        <Chip label="Em andamento" size="small" color="info" />
                      ) : event.invoicePaid ? (
                        <Chip icon={<CheckCircleIcon />} label="Paga" size="small" color="success" />
                      ) : event.invoiceIssued ? (
                        <Chip
                          label="Emitida"
                          size="small"
                          sx={{ bgcolor: '#B8F5C7', color: '#12603A', fontWeight: 700 }}
                        />
                      ) : (
                        <Chip label="Pendente" size="small" color="warning" />
                      )}
                      {event.invoiceNumber && (
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                          NF: {event.invoiceNumber}
                        </Typography>
                      )}
                      {event.nfse?.status && event.nfse.status !== 'autorizada' && (
                        <Typography variant="caption" display="block" color={event.nfse.status === 'rejeitada' || event.nfse.status === 'erro' ? 'error' : 'text.secondary'} sx={{ mt: 0.5 }}>
                          {nfseBusy(event.nfse.status) ? 'Processando na prefeitura' : event.nfse.errorMessage || 'Emissão recusada'}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top' }}>
                      <Stack spacing={0.75}>
                        <Box display="flex" justifyContent="space-between" gap={1}>
                          <Typography variant="caption" color="text.secondary">PIX</Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {formatCurrency(event.pagamentos?.pix?.totalAmount || 0)}
                          </Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" gap={1}>
                          <Typography variant="caption" color="text.secondary">Cartão</Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {formatCurrency(event.pagamentos?.cartao?.totalAmount || 0)}
                          </Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" gap={1}>
                          <Typography variant="caption" color="text.secondary">Outros</Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {formatCurrency(event.pagamentos?.outros?.totalAmount || 0)}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top' }}>
                      <Stack spacing={0.75}>
                        <Box display="flex" justifyContent="space-between" gap={1}>
                          <Typography variant="caption" color="text.secondary">Receita total</Typography>
                          <Typography variant="body2" fontWeight={700}>
                            {formatCurrency(event.totalAmountPaid)}
                          </Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" gap={1}>
                          <Typography variant="caption" color="text.secondary">Receita da paróquia</Typography>
                          <Typography variant="body2" fontWeight={700} color="primary.main">
                            {formatCurrency(event.parishFeeAmount)}
                          </Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" gap={1}>
                          <Typography variant="caption" color="text.secondary">Receita da plataforma ({event.feePercentage || 0}%)</Typography>
                          <Typography variant="body2" fontWeight={700} color="error.main">
                            {formatCurrency(event.platformFeeAmount || 0)}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell
                      sx={{ verticalAlign: 'top' }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Box display="flex" gap={0.75} flexDirection="column" sx={{ minWidth: 170 }}>
                    <Button
                      size="small"
                      variant="text"
                      startIcon={<VisibilityIcon />}
                      onClick={() => handleOpenDetails(event)}
                      fullWidth
                    >
                      Ver detalhes
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="secondary"
                      startIcon={backfillingSlug === event.eventSlug ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
                      onClick={() => backfillMutation.mutate(event.eventSlug)}
                      disabled={!!backfillingSlug}
                      fullWidth
                    >
                      {backfillingSlug === event.eventSlug ? 'Buscando...' : 'Atualizar formas'}
                    </Button>
                    {filters.status === 'em_curso' ? (
                      <Typography variant="caption" color="text.secondary">Aguardando término</Typography>
                    ) : (
                      <>
                        {!event.organizer.cpfCnpj && (
                          <Button
                            size="small"
                            variant="contained"
                            color="warning"
                            startIcon={<EmailIcon />}
                            onClick={() => handleRequestInvoiceData(event)}
                            sx={{ mb: 1 }}
                            fullWidth
                          >
                            Solicitar dados
                          </Button>
                        )}
                        {!event.invoiceIssued && event.organizer.cpfCnpj && !nfseBusy(event.nfse?.status) && (
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<ReceiptIcon />}
                            onClick={() => handleEmitInvoice(event)}
                            fullWidth
                          >
                            Emitir nota
                          </Button>
                        )}
                        {nfseBusy(event.nfse?.status) && (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={refreshNfseMutation.isPending ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
                            onClick={() => refreshNfseMutation.mutate(event.eventId)}
                            disabled={refreshNfseMutation.isPending}
                            fullWidth
                          >
                            Atualizar nota
                          </Button>
                        )}
                        {event.nfse?.status === 'autorizada' && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleDownloadDanfe(event.eventId)}
                            fullWidth
                          >
                            Baixar DANFE
                          </Button>
                        )}
                        {!event.invoiceIssued && event.organizer.cpfCnpj && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleRegisterInvoice(event)}
                            fullWidth
                          >
                            Registrar emissão
                          </Button>
                        )}
                        {event.invoiceIssued && !event.invoicePaid && (
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            startIcon={<PaymentIcon />}
                            onClick={() => handleMarkAsPaid(event)}
                            fullWidth
                          >
                            Marcar paga
                          </Button>
                        )}
                      </>
                    )}
                      </Box>
                    </TableCell>
                  </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>
            );
          })}
        </Stack>
      )}

      {/* Dialog de detalhes do evento (clique na linha) */}
      <Dialog
        open={detailsDialog}
        onClose={() => setDetailsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Detalhes para nota fiscal</DialogTitle>
        <DialogContent dividers>
          {selectedEvent && (
            <Stack spacing={2.5} sx={{ pt: 0.5 }}>
              <Box>
                <Typography variant="overline" color="text.secondary">Evento</Typography>
                <Typography variant="h6" fontWeight={700}>{selectedEvent.eventTitle}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Slug: {selectedEvent.eventSlug} · Finalizado em {formatDate(selectedEvent.endDate)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedEvent.totalRegistrations} inscrição(ões) paga(s)
                </Typography>
              </Box>

              <Divider />

              <Box>
                <Typography variant="overline" color="text.secondary">Tomador / Paróquia</Typography>
                <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Nome</Typography>
                    <Typography variant="body1" fontWeight={600}>{selectedEvent.organizer.name}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      {selectedEvent.organizer.personType === 'fisica' ? 'CPF' : selectedEvent.organizer.personType === 'juridica' ? 'CNPJ' : 'CPF/CNPJ'}
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {formatCpfCnpj(selectedEvent.organizer.cpfCnpj)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Email</Typography>
                    <Typography variant="body1">{selectedEvent.organizer.email || '—'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Telefone</Typography>
                    <Typography variant="body1">{selectedEvent.organizer.phone || '—'}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Endereço</Typography>
                    <Typography variant="body1">
                      {[
                        selectedEvent.organizer.address,
                        selectedEvent.organizer.city,
                        selectedEvent.organizer.state,
                        selectedEvent.organizer.zipCode,
                      ]
                        .filter(Boolean)
                        .join(' · ') || '—'}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              <Box>
                <Typography variant="overline" color="text.secondary">Valores</Typography>
                <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary">Receita total</Typography>
                    <Typography variant="h6" fontWeight={700}>
                      {formatCurrency(selectedEvent.totalAmountPaid)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary">Receita da paróquia</Typography>
                    <Typography variant="h6" fontWeight={700} color="primary.main">
                      {formatCurrency(selectedEvent.parishFeeAmount)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary">
                      Plataforma ({selectedEvent.feePercentage || 0}%)
                    </Typography>
                    <Typography variant="h6" fontWeight={700} color="error.main">
                      {formatCurrency(selectedEvent.platformFeeAmount || 0)}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              <Box>
                <Typography variant="overline" color="text.secondary">Meios de pagamento</Typography>
                <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary">
                      PIX ({selectedEvent.pagamentos?.pix?.count || 0})
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {formatCurrency(selectedEvent.pagamentos?.pix?.totalAmount || 0)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary">
                      Cartão ({selectedEvent.pagamentos?.cartao?.count || 0})
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {formatCurrency(selectedEvent.pagamentos?.cartao?.totalAmount || 0)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary">
                      Outros ({selectedEvent.pagamentos?.outros?.count || 0})
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {formatCurrency(selectedEvent.pagamentos?.outros?.totalAmount || 0)}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              <Box>
                <Typography variant="overline" color="text.secondary">Status da nota</Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                  {selectedEvent.invoicePaid ? (
                    <Chip icon={<CheckCircleIcon />} label="Paga" color="success" size="small" />
                  ) : selectedEvent.invoiceIssued ? (
                    <Chip label="Emitida" size="small" sx={{ bgcolor: '#B8F5C7', color: '#12603A', fontWeight: 700 }} />
                  ) : (
                    <Chip label="Pendente" color="warning" size="small" />
                  )}
                  {selectedEvent.invoiceNumber && (
                    <Typography variant="body2">NF: {selectedEvent.invoiceNumber}</Typography>
                  )}
                  {selectedEvent.invoiceDate && (
                    <Typography variant="body2" color="text.secondary">
                      Emitida em {formatDate(selectedEvent.invoiceDate)}
                    </Typography>
                  )}
                  {selectedEvent.paymentDate && (
                    <Typography variant="body2" color="text.secondary">
                      Paga em {formatDate(selectedEvent.paymentDate)}
                      {selectedEvent.paymentMethod ? ` · ${selectedEvent.paymentMethod}` : ''}
                    </Typography>
                  )}
                </Stack>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialog(false)}>Fechar</Button>
          {selectedEvent && !selectedEvent.invoiceIssued && selectedEvent.organizer.cpfCnpj && !nfseBusy(selectedEvent.nfse?.status) && (
            <Button
              variant="contained"
              startIcon={<ReceiptIcon />}
              onClick={() => {
                setDetailsDialog(false);
                handleEmitInvoice(selectedEvent);
              }}
            >
              Emitir nota
            </Button>
          )}
          {selectedEvent?.nfse?.status === 'autorizada' && (
            <Button variant="outlined" onClick={() => handleDownloadDanfe(selectedEvent.eventId)}>
              Baixar DANFE
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Dialog para solicitar/preencher dados fiscais */}
      <Dialog
        open={requestDataDialog.open}
        onClose={() => setRequestDataDialog({ open: false, event: null, mode: 'manual', cpfCnpj: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Dados fiscais da paróquia</DialogTitle>
        <DialogContent>
          {requestDataDialog.event && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Evento: {requestDataDialog.event.eventTitle}
              </Typography>

              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Button
                  variant={requestDataDialog.mode === 'manual' ? 'contained' : 'outlined'}
                  onClick={() => setRequestDataDialog((prev) => ({ ...prev, mode: 'manual' }))}
                >
                  Preencher manualmente
                </Button>
                <Button
                  variant={requestDataDialog.mode === 'email' ? 'contained' : 'outlined'}
                  onClick={() => setRequestDataDialog((prev) => ({ ...prev, mode: 'email' }))}
                >
                  Enviar por email
                </Button>
              </Box>

              {requestDataDialog.mode === 'manual' ? (
                <TextField
                  label="CPF/CNPJ"
                  fullWidth
                  size="small"
                  placeholder="Digite CPF ou CNPJ"
                  value={requestDataDialog.cpfCnpj}
                  onChange={(e) => setRequestDataDialog((prev) => ({ ...prev, cpfCnpj: e.target.value }))}
                  helperText="Ao salvar, o cadastro da paróquia será atualizado automaticamente."
                />
              ) : (
                <Alert severity="info">
                  Enviaremos o link para a paróquia preencher os dados. Quando ela responder, o cadastro será atualizado automaticamente.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRequestDataDialog({ open: false, event: null, mode: 'manual', cpfCnpj: '' })}>
            Cancelar
          </Button>
          {requestDataDialog.mode === 'manual' ? (
            <Button
              onClick={submitRequestDataManual}
              variant="contained"
              disabled={fillInvoiceDataManualMutation.isPending}
            >
              {fillInvoiceDataManualMutation.isPending ? <CircularProgress size={20} /> : 'Salvar dados'}
            </Button>
          ) : (
            <Button
              onClick={submitRequestDataByEmail}
              variant="contained"
              disabled={requestInvoiceDataMutation.isPending}
            >
              {requestInvoiceDataMutation.isPending ? <CircularProgress size={20} /> : 'Enviar solicitação'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Dialog para registrar emissão de nota */}
      <Dialog open={registerInvoiceDialog} onClose={() => setRegisterInvoiceDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Registrar Emissão de Nota Fiscal</DialogTitle>
        <DialogContent>
          {selectedEvent && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1, mb: 1 }}>
                Informações gerais da nota
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Evento: {selectedEvent.eventTitle}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Organizador: {selectedEvent.organizer.name}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Valor da Nota: <strong>{formatCurrency(selectedEvent.platformFeeAmount || 0)}</strong>
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Número da Nota Fiscal"
                    fullWidth
                    size="small"
                    value={registerInvoiceData.invoiceNumber}
                    onChange={(e) => setRegisterInvoiceData({ ...registerInvoiceData, invoiceNumber: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Data de Emissão"
                    type="date"
                    fullWidth
                    size="small"
                    value={registerInvoiceData.invoiceDate}
                    onChange={(e) => setRegisterInvoiceData({ ...registerInvoiceData, invoiceDate: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Observações"
                    fullWidth
                    multiline
                    rows={2}
                    size="small"
                    value={registerInvoiceData.notes}
                    onChange={(e) => setRegisterInvoiceData({ ...registerInvoiceData, notes: e.target.value })}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Enviar email com informações da nota para:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
                {DEFAULT_EMAILS.map((email) => (
                  <FormControlLabel
                    key={email}
                    control={
                      <Checkbox
                        checked={!!emailRecipients[email]}
                        onChange={(_, checked) => toggleEmailRecipient(email, checked)}
                      />
                    }
                    label={email}
                  />
                ))}
                {extraEmails.map((email) => (
                  <Box key={email} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!emailRecipients[email]}
                          onChange={(_, checked) => toggleEmailRecipient(email, checked)}
                        />
                      }
                      label={email}
                    />
                    <Button size="small" onClick={() => removeExtraEmail(email)} color="error">
                      Remover
                    </Button>
                  </Box>
                ))}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  size="small"
                  placeholder="Adicionar outro email"
                  type="email"
                  value={newEmailInput}
                  onChange={(e) => setNewEmailInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addExtraEmail())}
                  sx={{ flex: 1 }}
                />
                <Button variant="outlined" size="small" onClick={addExtraEmail}>
                  Adicionar
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRegisterInvoiceDialog(false)}>Cancelar</Button>
          <Button onClick={handleSubmitRegisterInvoice} variant="contained" disabled={registerInvoiceMutation.isPending}>
            {registerInvoiceMutation.isPending ? <CircularProgress size={24} /> : 'Registrar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para marcar como paga */}
      <Dialog open={markPaidDialog} onClose={() => setMarkPaidDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Marcar Nota como Paga</DialogTitle>
        <DialogContent>
          {selectedEvent && (
            <Box>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Evento: {selectedEvent.eventTitle}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Valor: <strong>{formatCurrency(selectedEvent.platformFeeAmount || 0)}</strong>
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Data do Pagamento"
                    type="date"
                    fullWidth
                    value={markPaidData.paymentDate}
                    onChange={(e) => setMarkPaidData({ ...markPaidData, paymentDate: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Método de Pagamento</InputLabel>
                    <Select
                      value={markPaidData.paymentMethod}
                      label="Método de Pagamento"
                      onChange={(e) => setMarkPaidData({ ...markPaidData, paymentMethod: e.target.value })}
                    >
                      <MenuItem value="pix">PIX</MenuItem>
                      <MenuItem value="credit_card">Cartão de Crédito</MenuItem>
                      <MenuItem value="debit_card">Cartão de Débito</MenuItem>
                      <MenuItem value="boleto">Boleto</MenuItem>
                      <MenuItem value="transfer">Transferência Bancária</MenuItem>
                      <MenuItem value="cash">Dinheiro</MenuItem>
                      <MenuItem value="other">Outro</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Observações"
                    fullWidth
                    multiline
                    rows={3}
                    value={markPaidData.notes}
                    onChange={(e) => setMarkPaidData({ ...markPaidData, notes: e.target.value })}
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMarkPaidDialog(false)}>Cancelar</Button>
          <Button onClick={handleSubmitMarkAsPaid} variant="contained" disabled={markAsPaidMutation.isPending}>
            {markAsPaidMutation.isPending ? <CircularProgress size={24} /> : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={emitDialog} onClose={() => setEmitDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Emitir NFS-e da taxa da plataforma</DialogTitle>
        <DialogContent>
          {selectedEvent && emitForm && (
            <Box sx={{ mt: 1 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                A nota é só da taxa da plataforma ({String(selectedEvent.feePercentage).replace('.', ',')}% do valor pago).
                O valor do ingresso não entra. A atividade 120801 exige os dados do evento.
              </Alert>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Valor da nota: <strong>{formatCurrency(selectedEvent.platformFeeAmount || 0)}</strong>
              </Typography>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Tomador (paróquia)</Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <TextField label="Nome" fullWidth size="small" value={emitForm.nome} onChange={(e) => setEmitForm({ ...emitForm, nome: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="CPF/CNPJ" fullWidth size="small" value={emitForm.documento} onChange={(e) => setEmitForm({ ...emitForm, documento: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="E-mail" fullWidth size="small" value={emitForm.email} onChange={(e) => setEmitForm({ ...emitForm, email: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="CEP" fullWidth size="small" value={emitForm.cep} onChange={(e) => setEmitForm({ ...emitForm, cep: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Logradouro" fullWidth size="small" value={emitForm.logradouro} onChange={(e) => setEmitForm({ ...emitForm, logradouro: e.target.value })} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField label="Número" fullWidth size="small" value={emitForm.numero} onChange={(e) => setEmitForm({ ...emitForm, numero: e.target.value })} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField label="UF" fullWidth size="small" value={emitForm.uf} onChange={(e) => setEmitForm({ ...emitForm, uf: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Bairro" fullWidth size="small" value={emitForm.bairro} onChange={(e) => setEmitForm({ ...emitForm, bairro: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Código IBGE (se o CEP não resolver)" fullWidth size="small" value={emitForm.codigoMunicipio} onChange={(e) => setEmitForm({ ...emitForm, codigoMunicipio: e.target.value })} />
                </Grid>
              </Grid>
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Evento (atividade 120801)</Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={12}>
                  <TextField label="Nome do evento" fullWidth size="small" value={emitForm.eventoNome} onChange={(e) => setEmitForm({ ...emitForm, eventoNome: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Início" type="date" fullWidth size="small" value={emitForm.dataInicio} onChange={(e) => setEmitForm({ ...emitForm, dataInicio: e.target.value })} InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Fim" type="date" fullWidth size="small" value={emitForm.dataFim} onChange={(e) => setEmitForm({ ...emitForm, dataFim: e.target.value })} InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField label="CEP do evento" fullWidth size="small" value={emitForm.eventoCep} onChange={(e) => setEmitForm({ ...emitForm, eventoCep: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <TextField label="Logradouro" fullWidth size="small" value={emitForm.eventoLogradouro} onChange={(e) => setEmitForm({ ...emitForm, eventoLogradouro: e.target.value })} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField label="Número" fullWidth size="small" value={emitForm.eventoNumero} onChange={(e) => setEmitForm({ ...emitForm, eventoNumero: e.target.value })} />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField label="Bairro" fullWidth size="small" value={emitForm.eventoBairro} onChange={(e) => setEmitForm({ ...emitForm, eventoBairro: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={8}>
                  <TextField label="Complemento" fullWidth size="small" value={emitForm.eventoComplemento} onChange={(e) => setEmitForm({ ...emitForm, eventoComplemento: e.target.value })} />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmitDialog(false)}>Cancelar</Button>
          <Button onClick={handleSubmitEmit} variant="contained" disabled={emitNfseMutation.isPending || !(selectedEvent?.platformFeeAmount)}>
            {emitNfseMutation.isPending ? <CircularProgress size={24} /> : 'Emitir nota'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default InvoiceIssuance;

